import { USER_ROLE } from './user-role.types';

/**
 * Roles con los que un usuario `sellers` puede entrar a un evento puntual.
 * Son valores de `USER_ROLE`: el rol elegido se guarda como rol activo y viaja como `?role=`.
 */
export const EVENT_ROLE = {
  SELLER: USER_ROLE.SELLER,
  SALES_MANAGER: USER_ROLE.SALES_MANAGER,
  ADMINISTRATOR: USER_ROLE.ADMINISTRATOR,
  GLOBAL_REPORT: USER_ROLE.GLOBAL_REPORT,
  SCANNER: USER_ROLE.SCANNER,
} as const;

export type EventRole = typeof EVENT_ROLE[keyof typeof EVENT_ROLE];

/** Orden de los botones de entrada en la lista de eventos. */
export const EVENT_ROLE_ORDER: readonly EventRole[] = [
  EVENT_ROLE.SELLER,
  EVENT_ROLE.SALES_MANAGER,
  EVENT_ROLE.ADMINISTRATOR,
  EVENT_ROLE.GLOBAL_REPORT,
  EVENT_ROLE.SCANNER,
];

/** Ícono de cada rol de entrada (botones de la lista y sheet del chip del header). */
export const EVENT_ROLE_ICONS: Readonly<Record<EventRole, string>> = {
  [EVENT_ROLE.SELLER]: 'ticket-outline',
  [EVENT_ROLE.SALES_MANAGER]: 'people-outline',
  [EVENT_ROLE.ADMINISTRATOR]: 'settings-outline',
  [EVENT_ROLE.GLOBAL_REPORT]: 'analytics-outline',
  [EVENT_ROLE.SCANNER]: 'qr-code-outline',
};

/** Tamaño de página común a todas las fuentes (`/sellers/events-past` usa 5 si no se envía). */
export const EVENT_ACCESS_PAGE_SIZE = 20;

/** Qué sabemos de un evento según las fuentes que lo trajeron. */
export interface EventRoleFlags {
  readonly canSell: boolean;
  readonly isBossEvent: boolean;
  readonly isAdminEvent: boolean;
  readonly canViewGlobalReport: boolean;
}

/** `capabilities` de `GET /api/seller-app/my-events`. */
export interface EventCapabilities {
  readonly can_sell: boolean;
  readonly can_share_microsite: boolean;
  readonly can_view_global_report: boolean;
  readonly can_view_marketing_report: boolean;
  readonly can_view_assistants: boolean;
  readonly can_view_scanner_report: boolean;
  readonly can_edit_products: boolean;
  readonly can_request_withdrawal: boolean;
  readonly is_petition_moderator: boolean;
}

/** Item de `GET /api/seller-app/my-events`. */
export interface MyEventItem {
  readonly id: number;
  readonly name: string;
  readonly slug: string;
  readonly initial_date: string;
  readonly finish_date: string;
  readonly cover_image: string | null;
  readonly active: number | boolean;
  readonly business: { readonly id: number; readonly name: string } | null;
  readonly access: { readonly via_products: boolean; readonly permissions: readonly string[] };
  readonly capabilities: EventCapabilities;
}

/**
 * Campos que usamos del `Event` resource de Laravel, que devuelven `/sales-manager/events*`,
 * `/seller-app/events/past|inactive` y `/sellers/events-past|events-inactive`.
 * `business` es el nombre (string). `global_report` solo llega con `with_permissions=true`.
 */
export interface ApiEventItem {
  readonly id: number;
  readonly name: string;
  readonly slug?: string | null;
  readonly initial_date: string;
  readonly finish_date: string;
  readonly cover_image?: string | null;
  readonly business?: string | null;
  readonly global_report?: boolean;
}

/** Fuente de listado de jefe/admin: define qué rol habilita el evento. */
export type ListingRole = 'administrator' | 'sales-manager';

/** Datos de un evento que muestra la lista, comunes a todas las fuentes. */
export interface EventSummary {
  readonly id: number;
  readonly name: string;
  readonly slug: string | null;
  readonly initialDate: string;
  readonly finishDate: string;
  readonly coverImage: string | null;
  readonly businessName: string | null;
}

/** Un evento tal como lo trajo una fuente. */
export interface EventSourceEntry {
  readonly event: EventSummary;
  readonly flags: Partial<EventRoleFlags>;
  readonly capabilities: EventCapabilities | null;
}

/** Evento ya unido entre fuentes, con sus roles de entrada. */
export interface EventAccessItem {
  readonly event: EventSummary;
  readonly flags: Partial<EventRoleFlags>;
  readonly capabilities: EventCapabilities | null;
  readonly roles: readonly EventRole[];
}

export type EventListTab = 'active' | 'ended' | 'inactive';

/** Página de Laravel tal como la devuelven las fuentes de eventos. */
export interface EventAccessPage<T> {
  readonly data: readonly T[];
  readonly meta: {
    readonly current_page: number;
    readonly last_page: number;
    readonly total?: number;
    readonly per_page?: number | string;
  };
}

/** Pestañas de eventos que no salen de my-events. */
export type PastEventTab = Exclude<EventListTab, 'active'>;
